import type { SupportTask } from "../Worklist/api";
import {
  formatDay,
  STALE_LAPSED_DAYS,
  STILL_SAVABLE_DAYS,
  type Severity,
} from "../Worklist/priority";

/**
 * Retention pipeline model — a SEPARATE surface from the worklist (the fire-queue).
 * Retention is PROACTIVE/outbound: expiring & lapsed subscriptions presented as a
 * renewal pipeline, draft-first. Nothing here sends a message — the CM opens waplify's
 * Care Manager chat and sends by hand; the stage buttons only RECORD where the customer
 * sits in the pipeline (picard tasks/crm-stage/).
 *
 * We reuse the worklist's expiry maths (STILL_SAVABLE_DAYS / STALE_LAPSED_DAYS and the
 * ending_at → days derivation) rather than re-deriving it, so a customer who reads
 * "expires in 1 day" here reads the same on the worklist P2 lane.
 */

const MS_PER_DAY = 86_400_000;

/**
 * The four LIVE pipeline stages, in order. Terminal outcomes
 * (renewed / not_interested / no_response) close the task server-side, so they never
 * reach this list. A task with no `crm` (or crm.stage === "at_risk") is At risk.
 */
export type RetentionStage = "at_risk" | "contacted" | "replied" | "call_back";

const STAGE_ORDER: RetentionStage[] = [
  "at_risk",
  "contacted",
  "replied",
  "call_back",
];

interface StageMeta {
  label: string;
  blurb: string;
}

export const STAGE_META: Record<RetentionStage, StageMeta> = {
  at_risk: {
    label: "At risk",
    blurb: "Expiring or recently lapsed — not yet reached out to.",
  },
  contacted: {
    label: "Contacted",
    blurb: "A renewal message has been sent; awaiting a reply.",
  },
  replied: {
    label: "Replied",
    blurb: "The customer wrote back — keep the conversation going.",
  },
  call_back: {
    label: "Call back",
    blurb: "Parked for a follow-up call at a better time.",
  },
};

/** Map picard's crm.stage onto a LIVE pipeline stage. Unknown/absent → At risk. */
function toStage(raw: string | null | undefined): RetentionStage {
  if (raw === "contacted" || raw === "replied" || raw === "call_back") return raw;
  return "at_risk";
}

export interface RetentionItem {
  key: string;
  taskId: number;
  name: string;
  /** first name for the personalised draft — null when we only have "Unknown customer" */
  firstName: string | null;
  mobile: string;
  plan: string;
  stage: RetentionStage;
  /** days until expiry; negative = already lapsed; null = no date on file */
  daysLeft: number | null;
  lapsed: boolean;
  /** "Expires in 2 days" / "Expires today" / "Lapsed 3 days ago" */
  expiryLabel: string;
  /** compact scan anchor, e.g. "2d" / "0d" */
  urgencyValue: string;
  urgencyLabel: string;
  /** "Ends 13 Jul" / "Ended 9 Jul" / a no-date fallback */
  meta: string;
  severity: Severity;
  /** the personalised, client-side renewal draft — COPY only, never auto-sent */
  draft: string;
  note: string | null;
  updatedBy: string | null;
  /** sort key within a group — soonest-expiry first (mirrors worklist buildP2) */
  rank: number;
}

export interface RetentionGroup {
  stage: RetentionStage;
  label: string;
  blurb: string;
  items: RetentionItem[];
}

/**
 * The renewal draft. Warm, no medical advice, personalised. A human copies this into
 * waplify and sends it — the template never touches the wire itself. Missing name
 * degrades to "Hi there"; a missing end date degrades to a dateless phrasing rather
 * than rendering "ends on null".
 */
export function renewalDraft(
  firstName: string | null,
  plan: string,
  lapsed: boolean,
  endDate: string | null
): string {
  const greeting = firstName ? `Hi ${firstName}` : "Hi there";
  const planLabel = plan && plan !== "Unknown plan" ? `${plan} ` : "";
  const clause = endDate
    ? `${lapsed ? "ended on" : "ends on"} ${endDate}`
    : lapsed
    ? "has ended"
    : "is ending soon";
  return `${greeting}, your Babynama ${planLabel}plan ${clause}. Renew here to keep your pediatric consultations going: https://babynama.com/renew — Team Babynama`;
}

function firstNameOf(name: string): string | null {
  if (!name || name === "Unknown customer") return null;
  const first = name.trim().split(/\s+/)[0];
  return first || null;
}

function buildItem(task: SupportTask, now: number): RetentionItem | null {
  const user = task.all_details?.user_details;
  const detail = task.all_details?.details?.[0];
  const endingAt = detail?.ending_at ?? null;

  // Same derivation the worklist uses: remarks.system.days_remaining when present,
  // otherwise ceil() off the subscription's ending_at.
  const fromRemarks = task.remarks?.system?.days_remaining;
  const days =
    typeof fromRemarks === "number"
      ? fromRemarks
      : endingAt
      ? Math.ceil((Date.parse(endingAt) - now) / MS_PER_DAY)
      : null;

  const lapsed = days !== null && days < 0;
  const plan = detail?.subscription_name || "Unknown plan";
  const name = user?.user_name || "Unknown customer";
  const first = firstNameOf(name);
  const endDay = formatDay(endingAt);

  let expiryLabel = "Subscription expired";
  let urgencyValue = "—";
  let urgencyLabel = "no date";
  if (days !== null) {
    if (days > 0) {
      expiryLabel = `Expires in ${days} day${days === 1 ? "" : "s"}`;
      urgencyValue = `${days}d`;
      urgencyLabel = "to expiry";
    } else if (days === 0) {
      expiryLabel = "Expires today";
      urgencyValue = "0d";
      urgencyLabel = "today";
    } else {
      const ago = Math.abs(days);
      expiryLabel = `Lapsed ${ago} day${ago === 1 ? "" : "s"} ago`;
      urgencyValue = `${ago}d`;
      urgencyLabel = "lapsed";
    }
  }

  const meta =
    days === null
      ? endDay
        ? `Ends ${endDay}`
        : "No subscription dates on file"
      : lapsed
      ? endDay
        ? `Ended ${endDay}`
        : "End date not on file"
      : endDay
      ? `Ends ${endDay}`
      : "End date not on file";

  // The whole section is proactive/amber, not the fire-red of the SLA queue. Still
  // savable (expiring today or within the window) reads warn; lapsed / far reads muted.
  const savable = days !== null && days >= 0 && days <= STILL_SAVABLE_DAYS;
  const severity: Severity =
    days === null ? "muted" : savable || days >= 0 ? "warning" : "muted";

  return {
    key: `retention-${task.id}`,
    taskId: task.id,
    name,
    firstName: first,
    mobile: user?.user_mobile || "—",
    plan,
    stage: toStage(task.crm?.stage),
    daysLeft: days,
    lapsed,
    expiryLabel,
    urgencyValue,
    urgencyLabel,
    meta,
    severity,
    draft: renewalDraft(first, plan, lapsed, endDay),
    note: task.crm?.note ?? null,
    updatedBy: task.crm?.updated_by ?? null,
    // Rank by SAVABILITY (identical to worklist buildP2): expiring outranks lapsed,
    // soonest-expiring first; among lapsed the most recent lapse is the most winnable.
    rank:
      days === null
        ? Number.NEGATIVE_INFINITY
        : days >= 0
        ? 1000 - days
        : 500 + days,
  };
}

/**
 * Turn the pending subscription_expired tasks into the four-stage renewal pipeline.
 * Drops stale rows (lapsed > STALE_LAPSED_DAYS ago) exactly like the worklist, so
 * 2024-era never-closed tasks don't bury live signal. Groups are returned in pipeline
 * order; within a group, soonest-expiry first.
 */
export function buildRetention(
  tasks: SupportTask[],
  now: number
): RetentionGroup[] {
  const items = tasks
    .map((t) => buildItem(t, now))
    .filter((it): it is RetentionItem => it !== null)
    .filter(
      (it) => it.daysLeft === null || it.daysLeft >= -STALE_LAPSED_DAYS
    );

  return STAGE_ORDER.map((stage) => ({
    stage,
    label: STAGE_META[stage].label,
    blurb: STAGE_META[stage].blurb,
    items: items
      .filter((it) => it.stage === stage)
      .sort((a, b) => b.rank - a.rank),
  }));
}

export interface RetentionSummary {
  total: number;
  /** still-savable: expiring today or within the savable window */
  savable: number;
  expiringToday: number;
  lapsed: number;
}

export function summarizeRetention(groups: RetentionGroup[]): RetentionSummary {
  const all = groups.flatMap((g) => g.items);
  return {
    total: all.length,
    savable: all.filter(
      (i) => i.daysLeft !== null && i.daysLeft >= 0 && i.daysLeft <= STILL_SAVABLE_DAYS
    ).length,
    expiringToday: all.filter((i) => i.daysLeft === 0).length,
    lapsed: all.filter((i) => i.lapsed).length,
  };
}
